import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { SocialProofWidgets } from '../components/ebook/SocialProofWidgets';
import {
  CheckCircle2,
  Star,
  Lock,
  Download,
  Sparkles,
  ShieldCheck,
  Quote,
  BookOpen,
  Loader2,
  Tag,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EBOOK_COVER =
  'https://customer-assets.emergentagent.com/job_authentic-creation/artifacts/psoq4m6s_Taking%20Your%20Power%20Back_20260211_213904_0000.png';

const AUTHOR_IMAGE =
  'https://customer-assets.emergentagent.com/job_e5aeb861-4698-4c3f-a54b-4b0b2b4402c1/artifacts/zvomh038_FB_IMG_1780263257283.jpg';
const AUTHOR_IMAGE_ABOUT =
  'https://customer-assets.emergentagent.com/job_e5aeb861-4698-4c3f-a54b-4b0b2b4402c1/artifacts/h163p57a_Screenshot_20230403_162902_Dropbox.jpg';

const TESTIMONIAL_SCREENSHOTS = [
  'https://customer-assets.emergentagent.com/job_1900c591-748a-4a5a-b5a8-5b3f7fcca452/artifacts/7f58t41k_testimonials.jpg',
  'https://customer-assets.emergentagent.com/job_1900c591-748a-4a5a-b5a8-5b3f7fcca452/artifacts/2eefqdjq_Testamonials.jpg',
];

const WRITTEN_TESTIMONIALS = [
  {
    name: 'Tamela Davis',
    quote:
      'Thank you for a beautiful session. I had two shifts — the first cleared the path to proceed with my choice. The second was so emotional, but as my hands touched, I felt my heart lighten and joy flowing through my body. Just amazing.',
  },
  {
    name: 'Dr. Tony Minervino, DC',
    quote:
      'Acknowledging all your parts, thanking them for their mission, and inviting them to be part of creating your new agenda is key to creating your End Result. Thank you Breah.',
  },
  {
    name: 'Roni Mani',
    quote:
      "Breah is an amazing superstar — the session was so fun with incredible energy. She explained the process in a way that made it feel magical. Truly awesome.",
  },
  {
    name: 'Rachael Rutherford',
    quote:
      "Loved your session. Thank you, Breah. I'm a new member loving every minute of it.",
  },
];

const WHAT_YOU_GET = [
  'The complete 5-Step Magnetic Mind Method',
  'The 7 Focus Points that block True Choices',
  'The 9 Unconscious Egoic Agendas (and how to release them)',
  'The Three Structures: Stuck, Oscillating & Creative',
  'Daily Power Practice + journal prompts',
  'Recoding worksheets to unplug old programming',
  'Bonus: Morning + Evening Power Routines',
];

const EbookLandingPage = () => {
  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(false);
  const [promo, setPromo] = useState(null);
  const [searchParams] = useSearchParams();
  const promoFromUrl = searchParams.get('promo');

  useEffect(() => {
    axios.get(`${API}/ebook/package`).then((res) => setPkg(res.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!promoFromUrl) return;
    axios
      .get(`${API}/ebook/promo/${encodeURIComponent(promoFromUrl)}`)
      .then((res) => setPromo(res.data))
      .catch(() => setPromo(null));
  }, [promoFromUrl]);

  const handleBuyNow = async () => {
    setLoading(true);
    try {
      const payload = {
        package_id: 'taking-your-power-back-ebook',
        origin_url: window.location.origin,
      };
      if (promo?.code) payload.promo_code = promo.code;
      const { data } = await axios.post(`${API}/checkout/session`, payload);
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error('Unable to start checkout. Please try again.');
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || 'Checkout failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const basePrice = pkg?.amount ?? 19.99;
  const original = pkg?.original_value ?? 59.99;
  const price = promo ? promo.final_amount : basePrice;
  const savings = (original - price).toFixed(2);

  return (
    <div className="min-h-screen bg-background" data-testid="ebook-landing-page">
      <SocialProofWidgets />

      {promo && (
        <div className="bg-gradient-to-r from-maroon via-primary-dark to-maroon text-primary-foreground border-b border-secondary/40" data-testid="promo-applied-banner">
          <div className="container mx-auto px-4 py-3 flex items-center justify-center gap-3 text-sm font-medium">
            <Tag className="w-4 h-4 text-secondary shrink-0" />
            <span>
              Welcome back! Code <span className="font-bold text-secondary tracking-wider mx-1">{promo.code}</span>
              applied —{' '}
              <span className="text-secondary font-bold">${promo.discount_amount.toFixed(2)} off</span>{' '}
              your order.
            </span>
          </div>
        </div>
      )}

      {/* HERO */}
      <section className="relative overflow-hidden hero-gradient">
        <div className="absolute inset-0 opacity-30 pointer-events-none">
          <div className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-secondary/20 blur-3xl" />
          <div className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-maroon/30 blur-3xl" />
        </div>

        <div className="container mx-auto px-4 py-16 md:py-24 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-primary-foreground"
            >
              <Badge className="bg-secondary/90 text-secondary-foreground hover:bg-secondary mb-5 px-3 py-1" data-testid="hero-badge">
                <Sparkles className="w-3.5 h-3.5 mr-1.5" />
                Brand New Release
              </Badge>

              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6">
                Stop Feeling Stuck.{' '}
                <span className="text-gradient-gold">Take Your Power Back.</span>
              </h1>

              <p className="text-lg md:text-xl text-primary-foreground/90 mb-8 leading-relaxed">
                The 5-Step Magnetic Mind Method to consciously create your life — unlock your
                personal power, dissolve old programming, and become the predominant creative force
                in your reality.
              </p>

              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6 mb-8">
                <div className="flex items-baseline gap-3">
                  <span className="text-5xl font-bold text-secondary font-display" data-testid="hero-price">
                    ${price}
                  </span>
                  <span className="text-2xl text-primary-foreground/60 line-through" data-testid="hero-original-price">
                    ${original}
                  </span>
                </div>
                <Badge className="bg-maroon hover:bg-maroon text-white border-0 px-3 py-1.5 text-sm">
                  Save ${savings} today
                </Badge>
              </div>

              <Button
                size="lg"
                className="btn-gold text-lg px-10 py-7 font-semibold rounded-full"
                onClick={handleBuyNow}
                disabled={loading}
                data-testid="hero-buy-now-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Starting checkout…
                  </>
                ) : (
                  <>
                    <Download className="w-5 h-5 mr-2" />
                    Get Instant Access — ${price}
                  </>
                )}
              </Button>

              <div className="flex flex-wrap items-center gap-5 mt-6 text-sm text-primary-foreground/80">
                <span className="flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-secondary" /> Secure Stripe checkout
                </span>
                <span className="flex items-center gap-1.5">
                  <Download className="w-4 h-4 text-secondary" /> Instant PDF delivery
                </span>
                <span className="flex items-center gap-1.5">
                  <Lock className="w-4 h-4 text-secondary" /> One-time payment
                </span>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="absolute inset-0 -m-6 rounded-3xl bg-secondary/20 blur-3xl" />
              <img
                src={EBOOK_COVER}
                alt="Taking Your Power Back E-Book"
                className="relative w-full max-w-md mx-auto rounded-2xl shadow-2xl shadow-black/40 ring-1 ring-secondary/40"
                data-testid="ebook-cover-image"
              />
              <div className="absolute -bottom-4 -right-4 bg-secondary text-secondary-foreground rounded-full px-4 py-2 font-display font-bold shadow-gold rotate-6">
                Instant Download
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <div className="gold-accent mx-auto mb-4" />
            <h2 className="font-display text-3xl md:text-5xl font-bold text-foreground mb-4">
              What&apos;s Inside Your E-Book
            </h2>
            <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
              A practical, step-by-step transformation framework — not theory, but a method you can
              use starting tomorrow morning.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
            {WHAT_YOU_GET.map((item, idx) => (
              <motion.div
                key={item}
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: idx * 0.05 }}
                className="flex items-start gap-3 p-5 rounded-xl feature-card-silver"
                data-testid={`feature-item-${idx}`}
              >
                <CheckCircle2 className="w-6 h-6 text-primary shrink-0 mt-0.5" />
                <span className="text-foreground font-medium">{item}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 bg-gradient-to-b from-background to-silver-shimmer/20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-14">
            <div className="maroon-accent mx-auto mb-4" />
            <h2 className="font-display text-3xl md:text-5xl font-bold text-foreground mb-4">
              Real Transformations
            </h2>
            <div className="flex items-center justify-center gap-1 mb-3" data-testid="rating-stars">
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} className="w-5 h-5 fill-secondary text-secondary" />
              ))}
              <span className="ml-2 text-muted-foreground font-medium">Loved by readers worldwide</span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto mb-12">
            {WRITTEN_TESTIMONIALS.map((t, idx) => (
              <motion.div
                key={t.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: idx * 0.07 }}
              >
                <Card className="p-6 card-shimmer h-full" data-testid={`testimonial-card-${idx}`}>
                  <Quote className="w-8 h-8 text-secondary mb-3" />
                  <p className="text-foreground italic leading-relaxed mb-4">&ldquo;{t.quote}&rdquo;</p>
                  <div className="flex items-center gap-3 pt-4 border-t border-silver/30">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary to-maroon flex items-center justify-center text-primary-foreground font-bold font-display">
                      {t.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">{t.name}</p>
                      <p className="text-xs text-muted-foreground">Verified Reader</p>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Screenshot testimonials */}
          <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {TESTIMONIAL_SCREENSHOTS.map((src, idx) => (
              <Card
                key={src}
                className="overflow-hidden border-silver/30 hover:shadow-silver transition-shadow"
                data-testid={`testimonial-screenshot-${idx}`}
              >
                <img
                  src={src}
                  alt={`Testimonial ${idx + 1}`}
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* AUTHOR */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-5xl mx-auto">
            <div className="order-2 lg:order-1">
              <div className="teal-accent mb-4" />
              <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
                Meet Breah Kettunen
              </h2>
              <p className="text-foreground/80 leading-relaxed mb-4">
                Breah is a certified Magnetic Mind Coach helping people around the world
                stop people-pleasing, set boundaries, and step into their authentic power. Her
                Superconscious Recode sessions have changed thousands of lives.
              </p>
              <p className="text-foreground/80 leading-relaxed">
                This E-Book distills her complete framework into a single, actionable guide — the
                same method she uses in her 1-on-1 coaching, now available in your hands.
              </p>
            </div>
            <div className="order-1 lg:order-2 relative">
              <img
                src={AUTHOR_IMAGE_ABOUT}
                alt="Breah Kettunen"
                className="rounded-2xl shadow-xl w-full object-cover max-h-[520px]"
                data-testid="author-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <Card className="max-w-3xl mx-auto p-10 md:p-14 text-center card-shimmer relative overflow-hidden">
            <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-secondary/20 blur-3xl" />
            <BookOpen className="w-12 h-12 mx-auto text-primary mb-4" />
            <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground mb-4">
              Ready to consciously create your life?
            </h2>
            <p className="text-base md:text-lg text-muted-foreground mb-8 max-w-xl mx-auto">
              One decision today separates you from a version of yourself that feels free,
              powerful, and unstuck. Get the complete guide instantly.
            </p>
            <div className="flex items-baseline justify-center gap-3 mb-6">
              <span className="text-5xl font-bold text-maroon font-display">${price}</span>
              <span className="text-xl text-muted-foreground line-through">${original}</span>
            </div>
            <Button
              size="lg"
              className="btn-maroon text-lg px-10 py-7 font-semibold rounded-full"
              onClick={handleBuyNow}
              disabled={loading}
              data-testid="final-cta-buy-now-button"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Starting checkout…
                </>
              ) : (
                <>
                  <Download className="w-5 h-5 mr-2" />
                  Get the E-Book Now
                </>
              )}
            </Button>
            <p className="text-xs text-muted-foreground mt-5 flex items-center justify-center gap-1.5">
              <Lock className="w-3.5 h-3.5" /> Secure checkout powered by Stripe
            </p>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default EbookLandingPage;
export { EbookLandingPage };
